/**
 * The desktop proof bridge for a server-issued Environment presentation
 * challenge. Challenge bytes are opaque: this module neither generates nor
 * parses them, and cannot convert any browser state into a locality claim.
 *
 * A plain browser deliberately has no bridge, so it receives `null`. A desktop
 * rejection (for example, a missing installation key or an invalid audience)
 * remains an error for the caller to surface as unavailable rather than being
 * mistaken for verified viewer binding.
 */

export type EnvironmentViewerChallengeSigner = {
  signEnvironmentViewerChallenge: (challengeBytes: string) => Promise<string>;
};

type EnvironmentViewerWindow = Window & {
  excellentEnvironmentViewer?: EnvironmentViewerChallengeSigner;
};

function viewerSigner(): EnvironmentViewerChallengeSigner | null {
  if (typeof window === "undefined") return null;
  const signer = (window as EnvironmentViewerWindow).excellentEnvironmentViewer;
  return typeof signer?.signEnvironmentViewerChallenge === "function" ? signer : null;
}

/** Whether this renderer has the narrowly-scoped Electron signing capability. */
export function environmentViewerBindingSupported(): boolean {
  return viewerSigner() !== null;
}

/**
 * Sign exactly the challenge bytes obtained from the server. `null` means this
 * is a normal browser renderer without the desktop capability; it never means
 * the viewer is local, remote, or otherwise verified.
 */
export function signServerIssuedEnvironmentViewerChallenge(
  challengeBytes: string,
): Promise<string | null> {
  if (typeof challengeBytes !== "string") {
    return Promise.reject(new TypeError("Environment viewer challenge must be server-issued string bytes."));
  }
  const signer = viewerSigner();
  return signer ? signer.signEnvironmentViewerChallenge(challengeBytes) : Promise.resolve(null);
}
